import { alpha, createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

export const clinicTheme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: {
      main: tokens.color.accent,
      dark: tokens.color.accentStrong,
      light: tokens.color.accentSoft,
      contrastText: tokens.color.white,
    },
    secondary: {
      main: tokens.color.eucalyptus,
      light: tokens.color.eucalyptusSoft,
      contrastText: tokens.color.white,
    },
    background: {
      default: tokens.color.canvas,
      paper: tokens.color.surface,
    },
    text: {
      primary: tokens.color.text,
      secondary: tokens.color.textMuted,
    },
    divider: tokens.color.border,
    success: { main: tokens.color.success, light: tokens.color.successSoft },
    warning: { main: tokens.color.warning, light: tokens.color.warningSoft },
    error: { main: tokens.color.danger, light: tokens.color.dangerSoft },
    info: { main: tokens.color.info, light: tokens.color.infoSoft },
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontSize: 'clamp(1.65rem, 2.2vw, 2rem)',
      fontWeight: 720,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.015em' },
    h3: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.8375rem', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 680, textTransform: 'none', letterSpacing: 0 },
    caption: { fontSize: '0.75rem', lineHeight: 1.45, color: tokens.color.textMuted },
  },
  shape: {
    borderRadius: tokens.radius.control,
  },
  spacing: 4,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        html: { minHeight: '100%', backgroundColor: tokens.color.canvas },
        body: {
          minHeight: '100%',
          margin: 0,
          backgroundColor: tokens.color.canvas,
          color: tokens.color.text,
          WebkitFontSmoothing: 'antialiased',
        },
        '#root': { minHeight: '100vh' },
        '::selection': {
          backgroundColor: tokens.color.accentSoft,
          color: tokens.color.accentStrong,
        },
        '*:focus-visible': {
          outline: `3px solid ${alpha(tokens.color.accent, 0.32)}`,
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            scrollBehavior: 'auto !important',
            transitionDuration: '0.01ms !important',
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 44,
          paddingInline: 18,
          borderRadius: tokens.radius.control,
          '&.Mui-focusVisible': { outline: `3px solid ${alpha(tokens.color.accent, 0.3)}` },
          '&.MuiButton-containedPrimary': {
            backgroundImage: `linear-gradient(135deg, ${tokens.color.accent} 0%, ${tokens.color.accentStrong} 100%)`,
          },
          '&.MuiButton-containedPrimary:hover': {
            backgroundImage: 'none',
            backgroundColor: tokens.color.accentStrong,
          },
        },
        outlined: { borderColor: tokens.color.borderStrong },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          '&:hover': { backgroundColor: tokens.color.subtle },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 44,
          backgroundColor: tokens.color.surface,
          borderRadius: tokens.radius.control,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.color.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.color.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.large,
          border: `1px solid ${tokens.color.border}`,
          boxShadow: tokens.shadow.floating,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderColor: tokens.color.border },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { height: 28, borderRadius: 999, fontWeight: 650 },
        label: { paddingInline: 10 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { minHeight: 42, padding: '8px 14px', textTransform: 'none', fontWeight: 650 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.color.border, padding: '12px 14px' },
        head: {
          color: tokens.color.textMuted,
          fontSize: '0.75rem',
          fontWeight: 720,
          backgroundColor: tokens.color.subtle,
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { borderRadius: 8, padding: '7px 10px', fontSize: '0.75rem' },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.control, boxShadow: tokens.shadow.floating },
      },
    },
  },
});
